<template>
    <div :style="cssProps" id="lx-online-booking" class="lx-online-booking" >
        <busy-modal :show="busy" :overlay="true"></busy-modal>
        <div
            class="lx-online-booking__body"
            v-if="initialized"
        >
            <div
                v-if="website.display_online_header"
                :class="{
                    'lx-online-booking__header': true,
                    'lx-online-booking__header__anchored':
                        website.anchored_menu,
                }"
            >
                <div
                    v-if="website.logo_thumbnail_url"
                    class="lx-online-booking__header__logo"
                >
                    <a
                        :href="
                            website.public_website_url
                                ? website.public_website_url
                                : 'javascript:void(0)'
                        "
                    >
                        <img
                            :src="website.logo_thumbnail_url"
                            alt="Company Logo"
                        />
                    </a>
                </div>
                <div class="lx-online-booking__header__text">
                    <h1 class="lx-online-booking__header__text__title">
                        <a
                            :href="
                                website.public_website_url
                                    ? website.public_website_url
                                    : 'javascript:void(0)'
                            "
                        >
                            {{ website.title }}
                        </a>
                    </h1>
                    <div class="lx-online-booking__header__text__address">
                        {{ website.one_line_address }}
                    </div>
                    <div
                        class="lx-online-booking__header__text__links"
                        v-if="website.public_website_url"
                    >
                        <a :href="website.public_website_url"
                            >Return to {{ website.public_website_title }}</a
                        >
                    </div>
                </div>
            </div>
            <div class="mt-2"></div>
            <router-view></router-view>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { formatResponseError } from '../../utils/api';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';

    export default {
        name: 'OnlineBooking',
        components: {
            BusyModal,
        },
        data() {
            return {
                initialized: false,
            };
        },
        computed: {
            ...mapGetters('onlineBooking', ['busy', 'cssVariables', 'website', 'reservation']),
            cssProps() {
                if (!this.cssVariables) {
                    return;
                }
                return {
                    '--header-address-display': !!this.cssVariables
                        .header_display_address
                        ? 'block'
                        : 'none',
                    '--header-background-color': this.cssVariables
                        .header_background_color,
                    '--header-border-bottom-color': this.cssVariables
                        .header_border_bottom_color,
                    '--header-color': this.cssVariables.header_font_color,
                    '--header-font-family': this.cssVariables.header_font,
                    '--header-title-font-family': this.cssVariables
                        .header_title_font,
                    '--header-title-display': !!this.cssVariables
                        .header_display_title
                        ? 'block'
                        : 'none',
                    '--header-text-float': this.cssVariables.header_text_float,
                    '--header-text-display-sm': !!this.cssVariables.header_logo
                        ? 'none'
                        : 'block',
                    '--header-logo-float': this.cssVariables.header_logo,
                    '--header-logo-display': !!this.cssVariables.header_logo
                        ? 'block'
                        : 'none',
                    '--header-links-display': !!this.cssVariables
                        .header_display_return
                        ? 'block'
                        : 'none',
                    '--header-text-align': this.cssVariables.header_text_align,
                    '--primary-background-color': this.cssVariables.primary_background_color,
                    '--body-background-color': this.cssVariables.body_background_color,
                    '--body-font-family': this.cssVariables.body_font,
                    '--body-font-color': this.cssVariables.body_font_color,
                    '--body-title-font-family': this.cssVariables
                        .body_title_font,
                    '--body-title-color': this.cssVariables
                        .body_title_color,
                    '--body-warning-color': this.cssVariables
                        .body_warning_color,
                };
            },
        },
        mounted() {
            (async () => {
                this.$store.commit('onlineBooking/gotBusy', true);
                try {
                    await this.fetchWebsite();
                    await Promise.all([
                        this.fetchCssVariables(),
                        this.maybeFetchRentalProperty(),
                        this.maybeCreateReservation(),
                    ]);
                } finally {
                    this.$store.commit('onlineBooking/gotBusy', false);
                    this.initialized = true;
                }
            })();

            window.addEventListener('message', this.handleMessage);
        },
        beforeUnmount() {
            window.removeEventListener('message', this.handleMessage);
        },
        methods: {
            handleBusy(busy) {
                this.busy = busy;
            },
            handleMessage(event) {
                if (event.data === 'refresh') {
                    this.fetchWebsite();
                    this.fetchCssVariables();
                } else if (event.data === 'refresh-css') {
                    this.fetchCssVariables();
                }
            },
            fetchWebsite() {
                return this.$store.dispatch('onlineBooking/fetchWebsite', {
                    url: this.$route.params.websiteUrl,
                    preview: !!this.$route.query.preview,
                });
            },
            fetchCssVariables() {
                return this.$store.dispatch('onlineBooking/fetchCssVariables', {
                    preview: !!this.$route.query.preview,
                });
            },
            async maybeFetchRentalProperty() {
                const rentalPropertyID = window.PROPERTY_ID;
                if (rentalPropertyID) {
                    try {
                        await this.$store.dispatch(
                            'onlineBooking/fetchRentalProperty',
                            {
                                id: rentalPropertyID,
                            }
                        );
                    } catch (err) {
                        this.errorToast({
                            text:
                                err.response.status === 404
                                    ? `Rental property #${rentalPropertyID} does not exist.`
                                    : `Could not load rental property #${rentalPropertyID}.`,
                        });
                    }
                }
            },
            async maybeCreateReservation() {
                if (this.$route.query.hasOwnProperty('selected_reservations')) {
                    const selectedReservations = [];
                    const tokens = this.$route.query[
                        'selected_reservations'
                    ].split('|');
                    tokens.forEach(token => {
                        const values = token.split(',');
                        if (values.length === 3) {
                            selectedReservations.push({
                                property_id: values[0],
                                from_date: values[1],
                                to_date: values[2],
                            });
                        }
                    });

                    const preview = !!this.$route.query.preview;
                    try {
                        if (this.$route.path.includes('online-quote')) {
                            await this.$store.dispatch(
                                'onlineBooking/createReservation',
                                {
                                    ...this.$route.query,
                                    selected_reservations: selectedReservations,
                                    withRentalAgreement: false,
                                    withOnlineQuoteText: true,
                                    withClient: false,
                                }
                            );
                            this.$router.replace({
                                name: 'online-booking-quote-form',
                                params: {
                                    websiteUrl: this.website.url,
                                    reservationId: this.reservation.id,
                                },
                            });
                        } else {
                            await this.$store.dispatch(
                                'onlineBooking/createReservation',
                                {
                                    ...this.$route.query,
                                    selected_reservations: selectedReservations,
                                }
                            );
                            this.$router.replace({
                                name: 'online-booking-checkout',
                                params: {
                                    websiteUrl: this.website.url,
                                },
                                ...(preview ? { query: { preview } } : {}),
                            });
                        }
                    } catch (err) {
                        const { generic } = formatResponseError(err);
                        this.errorToast({
                            text:
                                generic ||
                                'Something went wrong while creating reservation.',
                        });
                    }
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(*) {
        font-family: var(--body-font-family) !important;
    }

    :deep(.lx-card .card-header *) {
        font-family: var(--body-title-font-family) !important;
    }

    :deep(.lx-card .card-header span) {
        color: var(--body-title-color) !important;
    }

    :deep(div.text-warning) {
        color: var(--body-warning-color) !important;
    }

    :deep(div.bg-primary),
    :deep(.btn-primary),
    :deep(.lx-card > div.card-header),
    :deep(.lx-online-booking-checkout-complete-booking div.card-body) {
        background-color: var(--primary-background-color) !important;
        border-color: var(--primary-background-color) !important;
    }

    :deep(.lx-card > div.guest-verification-card-header) {
        background-color: #cf5329 !important;
        border-color: #cf5329 !important
    }

    :deep(.lx-card > div.guest-verification-success-card-header) {
        background-color: #28a745 !important;
        border-color: #28a745 !important
    }

    #lx-online-booking .lx-online-booking {
        &__body {
            min-height: 100vh;
            background-color: var(--body-background-color) !important;
            color: var(--body-font-color) !important;

            :deep(.btn-light),
            :deep(div.input-group select),
            :deep(div.input-group input),
            :deep(div.input-group textarea),
            :deep(section.input-wrapper input),
            :deep(button.lx-online-booking-checkout-gallery__show-all *),
            :deep(table *) {
                color: var(--body-font-color) !important;
            }
            
            :deep(section.input-wrapper label),
            ::deep(div.b-form-btn-label-control label),
            :deep(div.input-group input:placeholder-shown ~ label),
            ::deep(div.input-group textarea:placeholder-shown ~ label) {
                color: #737373 !important;
            }

            :deep(div.input-group select + label),
            :deep(div.input-group input:focus + label),
            :deep(div.input-group textarea:focus + label),
            :deep(section.input-wrapper .active-label) {
                color: #0d47a1 !important;
            }
        }

        &__header {
            padding-left: 15px;
            padding-right: 15px;
            width: 100%;
            overflow: auto;
            background-color: var(--header-background-color);
            color: var(--header-color);
            text-align: var(--header-text-align);
            * {
                font-family: var(--header-font-family) !important;
            }

            h1 a {
                font-family: var(--header-title-font-family) !important;
                color: var(--header-color);
            }

            &__logo {
                margin: 5px;
                float: var(--header-logo-float);
                display: var(--header-logo-display);
                img {
                    max-height: 100px;
                    max-width: 200px;
                    border: none;
                }
            }

            &__text {
                display: block;
                float: var(--header-text-float);

                &__title {
                    display: var(--header-title-display);
                }

                &__address {
                    display: var(--header-address-display);
                }

                &__links {
                    margin: 0.5rem 0 0.5rem 0;
                    display: var(--header-links-display);

                    a {
                        font-weight: bold;
                        text-decoration: underline;
                        color: var(--header-color);
                    }

                    @media print {
                        display: none !important;
                    }
                }

                @include media-breakpoint-down(md) {
                    display: var(--header-text-display-sm);
                }
            }

            &__anchored {
                position: sticky;
                z-index: 101;
                top: 0;
                
                @media print {
                    position: static;
                }
            }

            @include media-breakpoint-up(lg) {
                border-bottom: 1px solid var(--header-border-bottom-color);
            }
        }
    }
    @media print {
        .lx-online-booking__header__logo a[href]:after{
            content: "";
        }
        a[href^="#"]:after,
        a[href^="javascript:"]:after {
            content: "";
        }
        .hidden-print {
            display: none !important;
        }
    }
</style>
